import { HealthInsurerFormData } from '@/types/healthInsurerFormData'
import { isValidYyyyMmDdFormat } from '@/utils/dateUtils'
import {
  containsFullWidthAlphaNumeric,
  isHalfWidthNumeric,
  isValidPhoneNumber,
} from '@/utils/inputChecker'

export type ValidationErrors = Partial<
  Record<keyof HealthInsurerFormData | 'postalCode', string>
>

export const useHealthInsurerValidation = () => {
  const validate = (formData: HealthInsurerFormData): ValidationErrors => {
    const newErrors: ValidationErrors = {}

    if (!formData.startDate) {
      newErrors.startDate = '適用開始日は必須です'
    } else if (!isValidYyyyMmDdFormat(formData.startDate)) {
      newErrors.startDate = '適用開始日はYYYY-MM-DD形式で入力してください'
    } else {
      const [year, month, day] = formData.startDate.split('-').map(Number)
      const startDate = new Date(year, month - 1, day)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (startDate < today) {
        newErrors.startDate = '適用開始日は本日以降の日付で入力してください'
      }
    }

    if (!formData.insurerCode) {
      newErrors.insurerCode = '保険者番号は必須です'
    } else if (!isHalfWidthNumeric(formData.insurerCode)) {
      newErrors.insurerCode = '保険者番号は半角数字で入力してください'
    } else if (
      formData.insurerCode.length !== 6 &&
      formData.insurerCode.length !== 8
    ) {
      newErrors.insurerCode = '保険者番号は6桁または8桁で入力してください'
    }
    if (formData.branchCode && !isHalfWidthNumeric(formData.branchCode)) {
      newErrors.branchCode = '保険者管理枝番は半角数字で入力してください'
    } else if (formData.branchCode && formData.branchCode.length !== 2) {
      newErrors.branchCode = '保険者管理枝番は2桁で入力してください'
    }
    //TODO 1.1.0以降に精査して実装
    // } else if (
    //   formData.insuranceTypeCode !== '00' &&
    //   formData.insurerCode.slice(0, 2) !== formData.insuranceTypeCode
    // ) {
    //   newErrors.insurerCode =
    //     '保険者番号の初めの2桁は保険種別の法別番号と同じにしてください'
    // } else if (formData.insurerCode.slice(2, 4) !== formData.prefectureCode) {
    //   newErrors.insurerCode =
    //     '保険者番号の3,4桁目は都道府県番号と同じにしてください'
    // }
    if (
      formData.primaryInsurerCode &&
      !isHalfWidthNumeric(formData.primaryInsurerCode)
    ) {
      newErrors.primaryInsurerCode =
        '代表保険者番号は半角数字で入力してください'
    } else if (
      formData.primaryInsurerCode &&
      formData.primaryInsurerCode.length !== 6 &&
      formData.primaryInsurerCode.length !== 8
    ) {
      newErrors.primaryInsurerCode =
        '代表保険者番号は6桁または8桁で入力してください'
    }
    if (
      formData.primaryInsurerCodeAcumoxi &&
      !isHalfWidthNumeric(formData.primaryInsurerCodeAcumoxi)
    ) {
      newErrors.primaryInsurerCodeAcumoxi =
        '代表保険者番号（鍼灸）は半角数字で入力してください'
    } else if (
      formData.primaryInsurerCodeAcumoxi &&
      formData.primaryInsurerCodeAcumoxi.length !== 6 &&
      formData.primaryInsurerCodeAcumoxi.length !== 8
    ) {
      newErrors.primaryInsurerCodeAcumoxi =
        '代表保険者番号（鍼灸）は6桁または8桁で入力してください'
    }

    if (!formData.insuranceTypeCode) {
      newErrors.insuranceTypeCode = '保険種別は必須です'
    }
    if (!formData.prefectureCode) {
      newErrors.prefectureCode = '都道府県は必須です'
    }

    if (!formData.name) {
      newErrors.name = '保険者名は必須です'
    } else if (formData.name.length > 50) {
      newErrors.name = '保険者名は50文字以内で入力してください'
    }
    if (!formData.postalCode1 || !formData.postalCode2) {
      newErrors.postalCode = '郵便番号は必須です'
    } else if (
      !isHalfWidthNumeric(formData.postalCode1) ||
      !isHalfWidthNumeric(formData.postalCode2)
    ) {
      newErrors.postalCode = '郵便番号は半角数字で入力してください'
    } else if (formData.postalCode1.length !== 3 || formData.postalCode2.length !== 4) {
      newErrors.postalCode = '郵便番号は3桁-4桁の形式で入力してください'
    }
    if (!formData.addressLine1) {
      newErrors.addressLine1 = '住所１は必須です'
    } else if (formData.addressLine1.length > 50) {
      newErrors.addressLine1 = '住所１は50文字以内で入力してください'
    } else if (containsFullWidthAlphaNumeric(formData.addressLine1)) {
      newErrors.addressLine1 = '住所１の英数字は半角で入力してください'
    }

    if (formData.addressLine2 && formData.addressLine2.length > 50) {
      newErrors.addressLine2 = '住所２は50文字以内で入力してください'
    } else if (
      formData.addressLine2 &&
      containsFullWidthAlphaNumeric(formData.addressLine2)
    ) {
      newErrors.addressLine2 = '住所２の英数字は半角で入力してください'
    }

    if (formData.phoneNumber && !isValidPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber =
        '電話番号は半角数字、ハイフンありで入力してください'
    }
    if (formData.faxNumber && !isValidPhoneNumber(formData.faxNumber)) {
      newErrors.faxNumber = 'FAX番号は半角数字、ハイフンありで入力してください'
    }
    if (
      formData.insurerDestinationName &&
      formData.insurerDestinationName.length > 50
    ) {
      newErrors.insurerDestinationName =
        '保険者別送達先名は50文字以内で入力してください'
    }
    if (
      formData.insurerDestinationNameAcumoxi &&
      formData.insurerDestinationNameAcumoxi.length > 50
    ) {
      newErrors.insurerDestinationNameAcumoxi =
        '保険者別送達先名（鍼灸）は50文字以内で入力してください'
    }
    return newErrors
  }

  return { validate }
}
